/**
 * Hook para atividades de Cliente com paginação server-side
 * Usa useInfiniteQuery com cursor composto (created_at + id)
 * Busca atividades por customer_id E por deals vinculados via contact_id
 * 
 * @version 2.2.0
 * RELEASE: 2026-08-09 v2.2.0 - Modo CONTATO (contactId): timeline compartilhada
 *          entre contato e cliente (RPC get_customer_activities_paginated v1.3.0)
 * RELEASE: 2026-05-10 v2.0.0 - 100% server-side: anexos via get_activity_attachments_batch e mutations via RPCs complete/reopen/delete_deal_activity
 * RELEASE: 2026-03-16 v1.0.0 - Implementação inicial espelhando useDealActivitiesPaginated
 */
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import { useMemo } from 'react';
import type { CustomerActivity, CustomerActivityAttachment } from '@/hooks/useCustomerActivities';

// Logger do módulo
const logger = createLogger('CustomerActivitiesPaginated');

/** Tamanho da página para scroll infinito */
const PAGE_SIZE = 10;

/**
 * Cursor composto para paginação server-side
 */
interface CursorParam {
  cursor_created_at: string;
  cursor_id: string;
}

/**
 * Opções do hook
 */
interface CustomerActivitiesPaginatedOptions {
  enabled?: boolean;
  /** v2.2.0: ID do contato (modo Contato ou complemento do cliente) */
  contactId?: string | null;
}

/**
 * Hook para atividades de um cliente com paginação server-side
 * Substitui useCustomerActivities quando paginated=true
 */
export function useCustomerActivitiesPaginated(
  customerId: string | null,
  options?: CustomerActivitiesPaginatedOptions
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // v2.2.0: aceita cliente OU contato
  const contactId = options?.contactId ?? null;
  const isEnabled =
    (options?.enabled ?? true) && (!!customerId || !!contactId) && !!currentTenant?.id;

  // Query infinita com cursor composto
  const infiniteQuery = useInfiniteQuery({
    queryKey: ['customer-activities-paginated', customerId, contactId],
    queryFn: async ({ pageParam }) => {
      if ((!customerId && !contactId) || !currentTenant?.id) return [];

      const cursor = pageParam as CursorParam | null;
      logger.debug('Buscando página de atividades do cliente:', { customerId, contactId, cursor });

      // Chama RPC paginada
      const { data, error } = await supabase.rpc('get_customer_activities_paginated' as never, {
        p_customer_id: customerId,
        p_contact_id: contactId,
        p_limit: PAGE_SIZE,
        p_cursor_created_at: cursor?.cursor_created_at || null,
        p_cursor_id: cursor?.cursor_id || null,
        p_tenant_hint: currentTenant.id,
      } as never);

      if (error) {
        logger.error('Erro ao buscar atividades paginadas do cliente:', error.message);
        throw error;
      }

      // Transforma resultado flat da RPC em CustomerActivity
      const activities: CustomerActivity[] = ((data as any[]) || []).map((row: any) => ({
        id: row.id,
        activity_type: row.activity_type,
        title: row.title,
        description: row.description,
        scheduled_at: row.scheduled_at,
        completed_at: row.completed_at,
        is_system: row.is_system,
        created_at: row.created_at,
        deal: row.deal_id ? {
          id: row.deal_id,
          title: row.deal_title || '',
        } : null,
        performed_by_user: row.performed_by_id ? {
          id: row.performed_by_id,
          full_name: row.performed_by_name,
          avatar_url: row.performed_by_avatar,
        } : null,
        comments_count: row.comments_count || 0,
        conferencing_link: row.conferencing_link || null,
        new_value: row.new_value ?? null, // v2.1.0: payload estruturado
        attachments: [] as CustomerActivityAttachment[],
      }));

      // v2.0.0: anexos via RPC server-side (eliminado SELECT direto a crm_activity_attachments)
      if (activities.length > 0) {
        const activityIds = activities.map(a => a.id);
        const { data: attachmentsData, error: attErr } = await supabase.rpc(
          'get_activity_attachments_batch',
          { p_activity_ids: activityIds, p_tenant_hint: currentTenant.id }
        );
        if (attErr) {
          logger.error('Erro ao buscar anexos:', attErr.message);
        } else if (attachmentsData?.length) {
          const attachmentsByActivity = new Map<string, CustomerActivityAttachment[]>();
          (attachmentsData as any[]).forEach(att => {
            const list = attachmentsByActivity.get(att.activity_id) || [];
            list.push({
              id: att.id,
              file_name: att.file_name,
              file_type: att.file_type,
              file_size: att.file_size,
              public_url: att.public_url,
              thumbnail_url: att.thumbnail_url,
              mime_type: att.mime_type,
            });
            attachmentsByActivity.set(att.activity_id, list);
          });
          for (const act of activities) {
            act.attachments = attachmentsByActivity.get(act.id) || [];
          }
        }
      }

      logger.debug(`Página carregada: ${activities.length} atividades do cliente`);
      return activities;
    },
    initialPageParam: null as CursorParam | null,
    getNextPageParam: (lastPage) => {
      // Se retornou menos que PAGE_SIZE, não há mais páginas
      if (!lastPage || lastPage.length < PAGE_SIZE) return undefined;
      const lastItem = lastPage[lastPage.length - 1];
      return {
        cursor_created_at: lastItem.created_at,
        cursor_id: lastItem.id,
      } as CursorParam;
    },
    enabled: isEnabled,
    staleTime: 30 * 1000,
  });

  // Flatten de todas as páginas
  const allActivities = useMemo(() => {
    return infiniteQuery.data?.pages.flat() || [];
  }, [infiniteQuery.data?.pages]);

  /**
   * Invalida caches relacionados
   */
  const invalidateCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['customer-activities-paginated', customerId, contactId] });
    queryClient.invalidateQueries({ queryKey: ['customer-activities', customerId] });
    queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] });
  };

  // Mutation: concluir atividade (RPC)
  const completeActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('complete_deal_activity', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade concluída!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao concluir atividade: ${error.message}`);
    },
  });

  // Mutation: reabrir atividade (RPC)
  const reopenActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('reopen_deal_activity', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade reaberta!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao reabrir atividade: ${error.message}`);
    },
  });

  // Mutation: atualizar atividade manual (RPC update_deal_activity)
  // v2.1.0 - Paridade com useDealActivitiesPaginated
  const updateActivity = useMutation({
    mutationFn: async (input: { activity_id: string; title?: string; description?: string | null; scheduled_at?: string | null }) => {
      const payload: Record<string, unknown> = {};
      if (input.title !== undefined) payload.title = input.title;
      if (input.description !== undefined) payload.description = input.description;
      if (input.scheduled_at !== undefined) {
        payload.scheduled_at = input.scheduled_at
          ? new Date(input.scheduled_at).toISOString()
          : null;
      }
      const { error } = await supabase.rpc('update_deal_activity', {
        p_activity_id: input.activity_id,
        p_payload: payload as any,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade atualizada!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar atividade: ${error.message}`);
    },
  });

  // Mutation: deletar atividade (RPC)
  const deleteActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('delete_deal_activity', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade removida!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover atividade: ${error.message}`);
    },
  });

  return {
    activities: allActivities,
    isLoading: infiniteQuery.isLoading,
    error: infiniteQuery.error,
    completeActivity,
    reopenActivity,
    updateActivity,
    deleteActivity,
    refetch: infiniteQuery.refetch,
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
  };
}
